import {Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {VirtualFileService} from '@twpub/core/services/virtual-file.service';
import {NGXLogger} from 'ngx-logger';
import {GalleryComponent, GalleryItem, GalleryModule, ImageItem} from 'ng-gallery';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {NoImageComponent} from '../card-image/no-image/no-image.component';
import {hasPropChanges} from '@twpub/core/utils';

@Component({
  selector: 'pub-image-gallery',
  templateUrl: './image-gallery.component.html',
  styleUrls: ['./image-gallery.component.scss'],
  standalone: true,
  imports: [GalleryModule, NoImageComponent]
})
export class ImageGalleryComponent implements OnChanges, OnDestroy {
  @Input() entryId?: number = 0;
  @Input() fileIds: number[] = [];
  images: GalleryItem[] = [];
  loading: boolean = true;

  @ViewChild(GalleryComponent) gallery!: GalleryComponent;

  // Blob URLs created for the gallery images; revoked when the inputs change or the component is destroyed.
  private objectUrls: string[] = [];

  constructor(private logger: NGXLogger, private fileService: VirtualFileService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasPropChanges(changes, ['entryId', 'fileIds'])) {
      this.loadImages();
    }
  }

  ngOnDestroy(): void {
    this.revokeObjectUrls();
  }

  private loadImages() {
    this.loading = true;
    this.revokeObjectUrls();
    this.images = [];

    if (!this.fileIds?.length) {
      this.loading = false;
      return;
    }

    this.logger.debug('ImageGalleryComponent.loadImages:', {fileIds: this.fileIds});
    // Fetch through the authenticated HttpClient (img/gallery src can't carry the auth token)
    // and hand ng-gallery blob URLs instead of the raw /vfiles endpoints.
    const requests = this.fileIds.map(fileId =>
      forkJoin({
        content: this.fileService.getContentBlob(fileId),
        thumb: this.fileService.getContentBlob(fileId, true).pipe(catchError(() => of<Blob | undefined>(undefined)))
      }).pipe(map(({content, thumb}) => this.toImageItem(content, thumb)))
    );

    forkJoin(requests).subscribe({
      next: (items) => {
        this.images = items;
        this.loading = false;
      },
      error: (error) => {
        this.logger.error('Failed to load images', error);
        this.loading = false;
      }
    });
  }

  private toImageItem(content: Blob, thumb?: Blob): ImageItem {
    const src = URL.createObjectURL(content);
    this.objectUrls.push(src);
    if (thumb && thumb.size > 0) {
      const thumbUrl = URL.createObjectURL(thumb);
      this.objectUrls.push(thumbUrl);
      return new ImageItem({src, thumb: thumbUrl});
    }
    return new ImageItem({src, thumb: src});
  }

  private revokeObjectUrls() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }
}
